import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCalendar, FiClock, FiMapPin } from 'react-icons/fi';

// Afbeeldingen importeren
import boekenmarktImage from '../assets/images/boekenmarkt.jpg';
import bridgeImage from '../assets/images/bridgegoededoel.jpg';
import vredesposterImage from '../assets/images/vredesposter.jpg';
import hospiceImage from '../assets/images/hospicedignitas.jpg';
import ukraineImage from '../assets/images/ukraine.jpg';
import warmteImage from '../assets/images/warmteukraine.jpg';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Home: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-lions-dark text-white overflow-hidden">
        {/* <div className="absolute inset-0 z-0">
          <img 
            src="/images/hero-bg.jpg" 
            alt="Lions Club Hoorn De Doelen" 
            className="w-full h-full object-cover opacity-30"
          />
        </div> */}
        <div className="container-custom relative z-10 py-20 md:py-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              Welkom bij <span className="text-lions-yellow">Lions Club</span> Hoorn De Doelen
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Samen maken we een verschil in onze gemeenschap door het ondersteunen van lokale en internationale goede doelen.
            </motion.p>
            <motion.div 
              variants={fadeIn}
              className="flex flex-wrap gap-4"
            >
              <Link to="/over-ons" className="btn-primary">
                Meer over ons
              </Link>
              <Link to="/projecten" className="btn-secondary">
                Onze projecten
              </Link>
            </motion.div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-lions-dark to-transparent"></div>
      </section>

      {/* Missie Section */}
      <section className="section bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl md:text-4xl font-bold mb-6 text-lions-blue dark:text-lions-yellow"
            >
              Onze Missie
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg mb-8 dark:text-gray-300"
            >
              Lions Club Hoorn De Doelen zet zich in voor het verbeteren van de kwaliteit van leven van mensen in onze gemeenschap en wereldwijd. 
              We organiseren diverse activiteiten en evenementen om fondsen te werven voor goede doelen.
            </motion.p>
            <motion.div 
              variants={fadeIn}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12"
            >
              <div className="bg-lions-light dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-lions-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Humanitaire Hulp</h3>
                <p className="dark:text-gray-300">We bieden humanitaire hulp, zowel lokaal als wereldwijd, waar dat het hardst nodig is.</p>
              </div>
              <div className="bg-lions-light dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-lions-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Educatie</h3>
                <p className="dark:text-gray-300">We organiseren educatieve projecten zoals de Vredesposterwedstrijd voor kinderen.</p>
              </div>
              <div className="bg-lions-light dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-lions-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Gemeenschap</h3>
                <p className="dark:text-gray-300">We steunen lokale initiatieven en organisaties die een positieve impact hebben op onze gemeenschap.</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Evenementen Section */}
      <section className="section bg-lions-light dark:bg-gray-900">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div 
              variants={fadeIn}
              className="flex justify-between items-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-lions-blue dark:text-lions-yellow">Komende Evenementen</h2>
              <Link to="/agenda" className="flex items-center text-lions-blue dark:text-lions-yellow hover:underline">
                Alle evenementen <FiArrowRight className="ml-2" />
              </Link>
            </motion.div>
            <motion.div 
              variants={fadeIn}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {/* Evenement 1 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <img 
                  src={boekenmarktImage} 
                  alt="Boekenmarkt" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-lions-yellow text-lions-dark text-sm font-semibold rounded-full mb-4">
                    Fundraising
                  </span>
                  <h3 className="text-xl font-bold mb-2">Jaarlijkse Boekenmarkt</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Onze jaarlijkse boekenmarkt waar u tweedehands boeken kunt kopen voor het goede doel.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiCalendar className="mr-2" /> 12 april 2025
                    </div>
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiClock className="mr-2" /> 10:00 - 16:00 uur
                    </div>
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiMapPin className="mr-2" /> Turfhaven te Hoorn
                    </div>
                  </div>
                  <Link to="/agenda/boekenmarkt" className="btn-primary w-full text-center">
                    Meer informatie
                  </Link>
                </div>
              </div>

              {/* Evenement 2 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <img 
                  src={bridgeImage}
                  alt="Bridgen voor het Goede Doel" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-lions-yellow text-lions-dark text-sm font-semibold rounded-full mb-4">
                    Fundraising
                  </span>
                  <h3 className="text-xl font-bold mb-2">Bridgen voor het Goede Doel</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Een gezellige bridgemiddag georganiseerd door Lions Club Hoorn De Doelen en Bridgeclub Zwaag / Risdam.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiCalendar className="mr-2" /> 2 november 2025
                    </div>
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiClock className="mr-2" /> 13:00 - 17:00 uur
                    </div>
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiMapPin className="mr-2" /> De Witte Valk, Zwaag
                    </div>
                  </div>
                  <Link to="/agenda/bridgen" className="btn-primary w-full text-center">
                    Meer informatie
                  </Link>
                </div>
              </div>

              {/* Evenement 3 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <img 
                  src={vredesposterImage} 
                  alt="Vredesposterwedstrijd" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-lions-yellow text-lions-dark text-sm font-semibold rounded-full mb-4">
                    Educatie
                  </span>
                  <h3 className="text-xl font-bold mb-2">Vredesposterwedstrijd</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Een wedstrijd waarbij kinderen worden aangemoedigd om na te denken over vrede en dit uit te drukken in een poster.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiCalendar className="mr-2" /> September 2025
                    </div>
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiClock className="mr-2" /> Inschrijving open
                    </div>
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <FiMapPin className="mr-2" /> Scholen in Hoorn
                    </div>
                  </div>
                  <Link to="/projecten/vredesposter" className="btn-primary w-full text-center">
                    Meer informatie
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Nieuws Section */}
      <section className="section bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div 
              variants={fadeIn}
              className="flex justify-between items-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-lions-blue dark:text-lions-yellow">Laatste Nieuws</h2>
              <Link to="/nieuws" className="flex items-center text-lions-blue dark:text-lions-yellow hover:underline">
                Alle nieuwsberichten <FiArrowRight className="ml-2" />
              </Link>
            </motion.div>
            <motion.div 
              variants={fadeIn}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {/* Nieuws 1 */}
              <div className="bg-lions-light dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <img 
                  src={hospiceImage} 
                  alt="Hospice Dignitas" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">2 mei 2023</span>
                    <span className="inline-block px-2 py-1 bg-lions-blue text-white text-xs rounded-full">
                      Donatie
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Gasten Hospice in Hoorn kunnen zelf hun dossier bijhouden</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    De Ipads die Hospice Dignitas in Hoorn recent van serviceclub Lions Hoorn De Doelen cadeau kreeg, hebben inmiddels...
                  </p>
                  <Link to="/nieuws/hospice-ipads" className="text-lions-blue dark:text-lions-yellow hover:underline flex items-center">
                    Lees meer <FiArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>

              {/* Nieuws 2 */}
              <div className="bg-lions-light dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <img 
                  src={ukraineImage} 
                  alt="Oekraïne hulp" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">1 juli 2023</span>
                    <span className="inline-block px-2 py-1 bg-lions-blue text-white text-xs rounded-full">
                      Internationaal
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">€250.408 opbrengst voor Oekraïne</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Lions Clubs in Nederland hebben gezamenlijk een indrukwekkend bedrag opgehaald voor hulp aan Oekraïne...
                  </p>
                  <Link to="/nieuws/opbrengst-oekraine" className="text-lions-blue dark:text-lions-yellow hover:underline flex items-center">
                    Lees meer <FiArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>

              {/* Nieuws 3 */}
              <div className="bg-lions-light dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <img 
                  src={warmteImage} 
                  alt="Warmte voor Oekraïne" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">17 januari 2023</span>
                    <span className="inline-block px-2 py-1 bg-lions-blue text-white text-xs rounded-full">
                      Actie
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Laatste nieuws actie 'Warmte voor Oekraïne'</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    De actie 'Warmte voor Oekraïne' is in volle gang. Hier vindt u het laatste nieuws over deze belangrijke hulpactie...
                  </p>
                  <Link to="/nieuws/warmte-oekraine" className="text-lions-blue dark:text-lions-yellow hover:underline flex items-center">
                    Lees meer <FiArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-lions-blue text-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Doe mee en maak een verschil
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Word lid van Lions Club Hoorn De Doelen of steun onze projecten als sponsor. Samen kunnen we meer bereiken!
            </motion.p>
            <motion.div 
              variants={fadeIn}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/lid-worden" className="btn-secondary">
                Word lid
              </Link>
              <Link to="/sponsor-worden" className="bg-white text-lions-blue hover:bg-gray-100 font-bold py-2 px-4 rounded transition-colors duration-300">
                Word sponsor
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 